import { apiSlice } from './apiSlice';
import { RailwayStation, Airport, Driver, Vehicle, Carrier } from '../../types';

// =================== НОВЫЕ ТИПЫ ДЛЯ КОНТРАГЕНТОВ ===================

export interface Contractor {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string | null;
  inn?: string;
  kpp?: string | null;
  ogrn?: string | null;
  directorName?: string | null;
  bankName?: string | null;
  bik?: string | null;
  accountNumber?: string | null;
  corrAccount?: string | null;
  notes?: string | null;
  roles: ContractorRole[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ContractorRole = 'client' | 'expeditor' | 'shipper' | 'consignee';

export interface CreateContractorRequest {
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  directorName?: string;
  bankName?: string;
  bik?: string;
  accountNumber?: string;
  corrAccount?: string;
  roles: ContractorRole[];
}

// =================== ТИПЫ ДЛЯ СКЛАДОВ ===================

export interface Warehouse {
  id: number;
  name: string;
  city: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

export interface CreateWarehouseRequest {
  name: string;
  city: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

// =================== СТАРЫЕ ТИПЫ (для совместимости) ===================

// Типы справочников
export interface Client {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
}

export interface Expeditor {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

export interface Shipper {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
}

export interface Consignee {
  id: number;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  inn?: string;
}

/** Договор клиента (справочник для заявки) */
export interface ContractRow {
  id: number;
  number: string;
  date: string;
  clientId: number;
  prepaymentPercent: number | null;
  prepaymentDelayDays: number | null;
  postpaymentPercent: number | null;
  postpaymentDelayDays: number | null;
  paymentTermsText: string | null;
  createdAt: string;
}

export interface CreateContractRequest {
  clientId: number;
  number: string;
  date: string;
  prepaymentPercent?: number | null;
  prepaymentDelayDays?: number | null;
  postpaymentPercent?: number | null;
  postpaymentDelayDays?: number | null;
  paymentTermsText?: string | null;
}

export const referencesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // =================== НОВЫЕ API ДЛЯ КОНТРАГЕНТОВ ===================
    
    // Все контрагенты
    getAllContractors: builder.query<Contractor[], void>({
      query: () => '/references/contractors',
      transformResponse: (response: { success: boolean; data: Contractor[] }) => response.data,
      providesTags: ['Contractor'],
    }),

    // Контрагенты по роли
    getContractorsByRole: builder.query<Contractor[], ContractorRole>({
      query: (role) => `/references/contractors/role/${role}`,
      transformResponse: (response: { success: boolean; data: Contractor[] }) => response.data,
      providesTags: ['Contractor'],
    }),

    // Один контрагент (карточка)
    getContractorById: builder.query<Contractor, number>({
      query: (id) => `/references/contractors/${id}`,
      transformResponse: (response: { success: boolean; data: Contractor }) => response.data,
      providesTags: (_result, _err, id) => [{ type: 'Contractor', id }],
    }),

    // Создать контрагента
    createContractor: builder.mutation<Contractor, CreateContractorRequest>({
      query: (contractor) => ({
        url: '/references/contractors',
        method: 'POST',
        body: contractor,
      }),
      transformResponse: (response: { success: boolean; data: Contractor }) => response.data,
      invalidatesTags: ['Contractor'],
    }),

    // Обновить контрагента
    updateContractor: builder.mutation<Contractor, { id: number; data: CreateContractorRequest }>({
      query: ({ id, data }) => ({
        url: `/references/contractors/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Contractor }) => response.data,
      invalidatesTags: ['Contractor'],
    }),

    // Удалить контрагента
    deleteContractor: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/contractors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Contractor'],
    }),

    // =================== API ДЛЯ СКЛАДОВ ===================
    
    getWarehouses: builder.query<Warehouse[], void>({
      query: () => '/references/warehouses',
      transformResponse: (response: { success: boolean; data: Warehouse[] }) => response.data,
      providesTags: ['Warehouse'],
    }),
    
    getWarehouseById: builder.query<Warehouse, number>({
      query: (id) => `/references/warehouses/${id}`,
      transformResponse: (response: { success: boolean; data: Warehouse }) => response.data,
      providesTags: ['Warehouse'],
    }),
    
    createWarehouse: builder.mutation<Warehouse, CreateWarehouseRequest>({
      query: (warehouse) => ({
        url: '/references/warehouses',
        method: 'POST',
        body: warehouse,
      }),
      transformResponse: (response: { success: boolean; data: Warehouse }) => response.data,
      invalidatesTags: ['Warehouse'],
    }),
    
    updateWarehouse: builder.mutation<Warehouse, { id: number; data: CreateWarehouseRequest }>({
      query: ({ id, data }) => ({
        url: `/references/warehouses/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Warehouse }) => response.data,
      invalidatesTags: ['Warehouse'],
    }),
    
    deleteWarehouse: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/warehouses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Warehouse'],
    }),

    // =================== СТАРЫЕ API (для совместимости) ===================

    getContractsByClientId: builder.query<ContractRow[], number>({
      query: (clientId) => `/contracts?clientId=${clientId}`,
      transformResponse: (response: { success: boolean; data: ContractRow[] }) => response.data,
      providesTags: (_result, _err, clientId) => [{ type: 'Contract', id: String(clientId) }],
    }),

    createContract: builder.mutation<ContractRow, CreateContractRequest>({
      query: (body) => ({
        url: '/contracts',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: ContractRow }) => response.data,
      invalidatesTags: (_result, _err, arg) => [{ type: 'Contract', id: String(arg.clientId) }],
    }),

    // Клиенты
    getClients: builder.query<Client[], void>({
      query: () => '/references/clients',
      transformResponse: (response: { success: boolean; data: Client[] }) => response.data,
      providesTags: ['Client'],
    }),
    createClient: builder.mutation<Client, Partial<Client>>({
      query: (client) => ({
        url: '/references/clients',
        method: 'POST',
        body: client,
      }),
      transformResponse: (response: { success: boolean; data: Client }) => response.data,
      invalidatesTags: ['Client'],
    }),

    // Экспедиторы
    getExpeditors: builder.query<Expeditor[], void>({
      query: () => '/references/expeditors',
      transformResponse: (response: { success: boolean; data: Expeditor[] }) => response.data,
      providesTags: ['Expeditor'],
    }),
    createExpeditor: builder.mutation<Expeditor, Partial<Expeditor>>({
      query: (expeditor) => ({
        url: '/references/expeditors',
        method: 'POST',
        body: expeditor,
      }),
      transformResponse: (response: { success: boolean; data: Expeditor }) => response.data,
      invalidatesTags: ['Expeditor'],
    }),

    // Грузоотправители
    getShippers: builder.query<Shipper[], void>({
      query: () => '/references/shippers',
      transformResponse: (response: { success: boolean; data: Shipper[] }) => response.data,
      providesTags: ['Shipper'],
    }),
    createShipper: builder.mutation<Shipper, Partial<Shipper>>({
      query: (shipper) => ({
        url: '/references/shippers',
        method: 'POST',
        body: shipper,
      }),
      transformResponse: (response: { success: boolean; data: Shipper }) => response.data,
      invalidatesTags: ['Shipper'],
    }),

    // Грузополучатели
    getConsignees: builder.query<Consignee[], void>({
      query: () => '/references/consignees',
      transformResponse: (response: { success: boolean; data: Consignee[] }) => response.data,
      providesTags: ['Consignee'],
    }),
    createConsignee: builder.mutation<Consignee, Partial<Consignee>>({
      query: (consignee) => ({
        url: '/references/consignees',
        method: 'POST',
        body: consignee,
      }),
      transformResponse: (response: { success: boolean; data: Consignee }) => response.data,
      invalidatesTags: ['Consignee'],
    }),

    // =================== ЖЕЛЕЗНОДОРОЖНЫЕ СТАНЦИИ ===================
    
    getRailwayStations: builder.query<RailwayStation[], void>({
      query: () => '/references/railway-stations',
      transformResponse: (response: { success: boolean; data: RailwayStation[] }) => response.data,
      providesTags: ['RailwayStation'],
    }),
    
    getRailwayStationById: builder.query<RailwayStation, number>({
      query: (id) => `/references/railway-stations/${id}`,
      transformResponse: (response: { success: boolean; data: RailwayStation }) => response.data,
      providesTags: ['RailwayStation'],
    }),
    
    createRailwayStation: builder.mutation<RailwayStation, Partial<RailwayStation>>({
      query: (station) => ({
        url: '/references/railway-stations',
        method: 'POST',
        body: station,
      }),
      transformResponse: (response: { success: boolean; data: RailwayStation }) => response.data,
      invalidatesTags: ['RailwayStation'],
    }),
    
    updateRailwayStation: builder.mutation<RailwayStation, { id: number; data: Partial<RailwayStation> }>({
      query: ({ id, data }) => ({
        url: `/references/railway-stations/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: RailwayStation }) => response.data,
      invalidatesTags: ['RailwayStation'],
    }),
    
    deleteRailwayStation: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/railway-stations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RailwayStation'],
    }),

    // =================== АЭРОПОРТЫ ===================
    
    getAirports: builder.query<Airport[], void>({
      query: () => '/references/airports',
      transformResponse: (response: { success: boolean; data: Airport[] }) => response.data,
      providesTags: ['Airport'],
    }),
    
    getAirportById: builder.query<Airport, number>({
      query: (id) => `/references/airports/${id}`,
      transformResponse: (response: { success: boolean; data: Airport }) => response.data,
      providesTags: ['Airport'],
    }),
    
    createAirport: builder.mutation<Airport, Partial<Airport>>({
      query: (airport) => ({
        url: '/references/airports',
        method: 'POST',
        body: airport,
      }),
      transformResponse: (response: { success: boolean; data: Airport }) => response.data,
      invalidatesTags: ['Airport'],
    }),
    
    updateAirport: builder.mutation<Airport, { id: number; data: Partial<Airport> }>({
      query: ({ id, data }) => ({
        url: `/references/airports/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Airport }) => response.data,
      invalidatesTags: ['Airport'],
    }),
    
    deleteAirport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/airports/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Airport'],
    }),

    // =================== ВОДИТЕЛИ ===================
    
    getDrivers: builder.query<Driver[], void>({
      query: () => '/references/drivers',
      transformResponse: (response: { success: boolean; data: Driver[] }) => response.data,
      providesTags: ['Driver'],
    }),
    
    getDriverById: builder.query<Driver, number>({
      query: (id) => `/references/drivers/${id}`,
      transformResponse: (response: { success: boolean; data: Driver }) => response.data,
      providesTags: ['Driver'],
    }),
    
    createDriver: builder.mutation<Driver, Partial<Driver>>({
      query: (driver) => ({
        url: '/references/drivers',
        method: 'POST',
        body: driver,
      }),
      transformResponse: (response: { success: boolean; data: Driver }) => response.data,
      invalidatesTags: ['Driver'],
    }),
    
    updateDriver: builder.mutation<Driver, { id: number; data: Partial<Driver> }>({
      query: ({ id, data }) => ({
        url: `/references/drivers/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Driver }) => response.data,
      invalidatesTags: ['Driver'],
    }),
    
    deleteDriver: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/drivers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver'],
    }),

    // =================== ТРАНСПОРТНЫЕ СРЕДСТВА ===================
    
    getVehicles: builder.query<Vehicle[], void>({
      query: () => '/references/vehicles',
      transformResponse: (response: { success: boolean; data: Vehicle[] }) => response.data,
      providesTags: ['Vehicle'],
    }),
    
    getVehicleById: builder.query<Vehicle, number>({
      query: (id) => `/references/vehicles/${id}`,
      transformResponse: (response: { success: boolean; data: Vehicle }) => response.data,
      providesTags: ['Vehicle'],
    }),
    
    createVehicle: builder.mutation<Vehicle, Partial<Vehicle>>({
      query: (vehicle) => ({
        url: '/references/vehicles',
        method: 'POST',
        body: vehicle,
      }),
      transformResponse: (response: { success: boolean; data: Vehicle }) => response.data,
      invalidatesTags: ['Vehicle'],
    }),
    
    updateVehicle: builder.mutation<Vehicle, { id: number; data: Partial<Vehicle> }>({
      query: ({ id, data }) => ({
        url: `/references/vehicles/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Vehicle }) => response.data,
      invalidatesTags: ['Vehicle'],
    }),
    
    deleteVehicle: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/vehicles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicle'],
    }),

    // =================== ПЕРЕВОЗЧИКИ ===================
    
    getCarriers: builder.query<Carrier[], void>({
      query: () => '/references/carriers',
      transformResponse: (response: { success: boolean; data: Carrier[] }) => response.data,
      providesTags: ['Carrier'],
    }),
    
    getCarrierById: builder.query<Carrier, number>({
      query: (id) => `/references/carriers/${id}`,
      transformResponse: (response: { success: boolean; data: Carrier }) => response.data,
      providesTags: ['Carrier'],
    }),
    
    createCarrier: builder.mutation<Carrier, Partial<Carrier>>({
      query: (carrier) => ({
        url: '/references/carriers',
        method: 'POST',
        body: carrier,
      }),
      transformResponse: (response: { success: boolean; data: Carrier }) => response.data,
      invalidatesTags: ['Carrier'],
    }),
    
    updateCarrier: builder.mutation<Carrier, { id: number; data: Partial<Carrier> }>({
      query: ({ id, data }) => ({
        url: `/references/carriers/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Carrier }) => response.data,
      invalidatesTags: ['Carrier'],
    }),
    
    deleteCarrier: builder.mutation<void, number>({
      query: (id) => ({
        url: `/references/carriers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Carrier'],
    }),
  }),
});

export const {
  useGetContractsByClientIdQuery,
  useCreateContractMutation,
  useGetClientsQuery,
  useCreateClientMutation,
  useGetExpeditorsQuery,
  useCreateExpeditorMutation,
  useGetShippersQuery,
  useCreateShipperMutation,
  useGetConsigneesQuery,
  useCreateConsigneeMutation,
  useGetAllContractorsQuery,
  useLazyGetAllContractorsQuery,
  useGetContractorsByRoleQuery,
  useGetContractorByIdQuery,
  useLazyGetContractorByIdQuery,
  useCreateContractorMutation,
  useUpdateContractorMutation,
  useDeleteContractorMutation,
  useGetWarehousesQuery,
  useGetWarehouseByIdQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  useGetRailwayStationsQuery,
  useGetRailwayStationByIdQuery,
  useCreateRailwayStationMutation,
  useUpdateRailwayStationMutation,
  useDeleteRailwayStationMutation,
  useGetAirportsQuery,
  useGetAirportByIdQuery,
  useCreateAirportMutation,
  useUpdateAirportMutation,
  useDeleteAirportMutation,
  useGetDriversQuery,
  useLazyGetDriversQuery,
  useGetDriverByIdQuery,
  useLazyGetDriverByIdQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
  useGetVehiclesQuery,
  useGetVehicleByIdQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleMutation,
  useGetCarriersQuery,
  useLazyGetCarriersQuery,
  useGetCarrierByIdQuery,
  useLazyGetCarrierByIdQuery,
  useCreateCarrierMutation,
  useUpdateCarrierMutation,
  useDeleteCarrierMutation,
} = referencesApi;

export type { Airport, Carrier, Driver, RailwayStation, Vehicle }; 